/**
 * THE PLANTOR — NATIVE MIX & MATCH BUNDLE & FESTIVAL ENGINE
 * Assets: assets/plantor-mix-match.js
 */

(function () {
  'use strict';

  if (typeof window !== 'undefined' && window._plantorMixMatchLoaded) {
    return;
  }
  if (typeof window !== 'undefined') {
    window._plantorMixMatchLoaded = true;
  }

  // Indian Rupee & Money Formatting Helper (clean whole rupees, zero trailing .00)
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '₹0';
    const rupees = Math.round(cents / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  // ---------------------------------------------------------------------------
  // 1. LIVE URGENCY COUNTDOWN TIMER (PDP Banner & Builder Section)
  // ---------------------------------------------------------------------------
  const CountdownEngine = {
    init: function () {
      const timers = document.querySelectorAll('[data-mix-match-countdown]');
      if (!timers.length) return;

      timers.forEach(timerEl => {
        const endTimeStr = timerEl.getAttribute('data-end-time');
        // If end_time is empty, run evergreen / continuously (no timer)
        if (!endTimeStr || endTimeStr.trim() === '') {
          timerEl.style.display = 'none';
          return;
        }

        let endTimestamp = Date.parse(endTimeStr.replace(/-/g, '/'));
        if (isNaN(endTimestamp)) {
          endTimestamp = Date.parse(endTimeStr);
        }
        if (isNaN(endTimestamp)) {
          timerEl.style.display = 'none';
          return;
        }

        const updateClock = () => {
          const now = Date.now();
          const distance = endTimestamp - now;

          if (distance <= 0) {
            // Expired: auto-terminate and hide
            const pdpBanner = timerEl.closest('[data-mix-match-pdp-banner]');
            if (pdpBanner) {
              pdpBanner.style.transition = 'opacity 0.4s ease';
              pdpBanner.style.opacity = '0';
              setTimeout(() => { pdpBanner.remove(); }, 400);
            }
            const sectionWrap = timerEl.closest('.plantor-mix-match-section');
            if (sectionWrap) {
              sectionWrap.style.display = 'none';
            }
            return false;
          }

          const days = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const mins = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((distance % (1000 * 60)) / 1000);

          const daysEl = timerEl.querySelector('[data-days]');
          const hoursEl = timerEl.querySelector('[data-hours]');
          const minsEl = timerEl.querySelector('[data-mins]');
          const secsEl = timerEl.querySelector('[data-secs]');

          if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
          if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
          if (minsEl) minsEl.textContent = String(mins).padStart(2, '0');
          if (secsEl) secsEl.textContent = String(secs).padStart(2, '0');

          const fullTextEl = timerEl.querySelector('[data-clock-text]');
          if (fullTextEl) {
            fullTextEl.textContent = `${days}d ${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
          }

          return true;
        };

        if (updateClock()) {
          const intervalId = setInterval(() => {
            if (!updateClock()) {
              clearInterval(intervalId);
            }
          }, 1000);
        }
      });
    }
  };

  // ---------------------------------------------------------------------------
  // 2. MIX & MATCH BUNDLE BUILDER
  // ---------------------------------------------------------------------------
  function MixMatchBuilder(container) {
    this.container = container;
    this.items = new Map(); // variantId -> item data

    // Configuration from DOM attributes
    this.isDrawerMode = (container.getAttribute('data-display-mode') === 'drawer');
    this.badgeText = container.getAttribute('data-badge-text') || 'Buy 5 for ₹999 | Buy 10 for ₹1,250';

    this.tier1Qty = parseInt(container.getAttribute('data-tier1-qty'), 10) || 5;
    this.tier1Type = container.getAttribute('data-tier1-type') || 'fixed_price';
    this.tier1Val = parseFloat(container.getAttribute('data-tier1-val')) || 2599;
    if (this.tier1Val >= 100 || this.tier1Type === 'fixed_amount') {
      this.tier1Type = 'fixed_price';
    }

    const rawT2Qty = container.getAttribute('data-tier2-qty');
    this.tier2Qty = (rawT2Qty !== null && rawT2Qty !== '') ? (parseInt(rawT2Qty, 10) || 0) : 0;
    this.tier2Type = container.getAttribute('data-tier2-type') || 'fixed_price';
    const rawT2Val = container.getAttribute('data-tier2-val');
    this.tier2Val = (rawT2Val !== null && rawT2Val !== '') ? (parseFloat(rawT2Val) || 0) : 0;
    if (this.tier2Val >= 100 || this.tier2Type === 'fixed_amount') {
      this.tier2Type = 'fixed_price';
    }

    this.tier3Qty = parseInt(container.getAttribute('data-tier3-qty'), 10) || 0;
    this.tier3Type = container.getAttribute('data-tier3-type') || 'percentage';
    this.tier3Val = parseFloat(container.getAttribute('data-tier3-val')) || 0;

    this.festivalName = container.getAttribute('data-festival-name') || 'Festive Bundle';
    this.campaignTitle = container.getAttribute('data-campaign-title') || this.festivalName;

    // In Drawer Mode: setup drawer, overlay, and floating trigger dock
    if (this.isDrawerMode) {
      this.floatingDock = container.querySelector('#PlantorComboFloatingDock');
      if (this.floatingDock && this.floatingDock.parentElement !== document.body) {
        document.body.appendChild(this.floatingDock);
      }
      this.floatingCount = this.floatingDock ? this.floatingDock.querySelector('[data-mm-floating-count]') : null;
      this.floatingSubtitle = this.floatingDock ? this.floatingDock.querySelector('[data-mm-floating-subtitle]') : null;
      this.floatingCtaText = this.floatingDock ? this.floatingDock.querySelector('[data-mm-floating-cta-text]') : null;

      this.drawerOverlay = container.querySelector('#PlantorComboDrawerOverlay');
      if (this.drawerOverlay && this.drawerOverlay.parentElement !== document.body) {
        document.body.appendChild(this.drawerOverlay);
      }
      this.drawer = container.querySelector('#PlantorComboDrawer');
      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }
    } else {
      this.floatingDock = null;
      this.floatingCount = null;
      this.floatingSubtitle = null;
      this.floatingCtaText = null;
      this.drawerOverlay = null;
      this.drawer = null;
    }

    // Tray DOM elements
    this.tray = (this.drawer ? this.drawer.querySelector('.plantor-mm-tray') : null) || container.querySelector('.plantor-mm-tray');
    // Only teleport tray to body in INLINE mode (in drawer mode, it stays pinned inside drawer footer)
    if (!this.isDrawerMode && this.tray && this.tray.parentElement !== document.body) {
      document.body.appendChild(this.tray);
    }

    this.traySlots = this.tray ? this.tray.querySelector('[data-mm-slots]') : null;
    this.trayCountTitle = this.tray ? this.tray.querySelector('[data-mm-count-title]') : null;
    this.trayCountSub = this.tray ? this.tray.querySelector('[data-mm-count-sub]') : null;
    this.trayNudgeText = this.tray ? this.tray.querySelector('[data-mm-nudge-text]') : null;
    this.trayProgressFill = this.tray ? this.tray.querySelector('[data-mm-progress-fill]') : null;
    this.trayComparePrice = this.tray ? this.tray.querySelector('[data-mm-compare-price]') : null;
    this.trayDealPrice = this.tray ? this.tray.querySelector('[data-mm-deal-price]') : null;
    this.traySavingsBadge = this.tray ? this.tray.querySelector('[data-mm-savings-badge]') : null;
    this.traySubmitBtn = this.tray ? this.tray.querySelector('[data-mm-submit]') : null;

    // Lightbox Modal DOM elements
    this.lightbox = container.querySelector('#plantor-mm-lightbox');
    if (this.lightbox && this.lightbox.parentElement !== document.body) {
      document.body.appendChild(this.lightbox);
    }
    this.currentLightboxCard = null;
    this.lightboxGallery = this.lightbox ? this.lightbox.querySelector('[data-mm-lightbox-gallery]') : null;
    this.lightboxTrack = this.lightbox ? this.lightbox.querySelector('[data-mm-lightbox-track]') : null;
    this.lightboxPrev = this.lightbox ? this.lightbox.querySelector('[data-mm-lightbox-prev]') : null;
    this.lightboxNext = this.lightbox ? this.lightbox.querySelector('[data-mm-lightbox-next]') : null;
    this.lightboxDots = this.lightbox ? this.lightbox.querySelector('[data-mm-lightbox-dots]') : null;
    this.currentSlideIndex = 0;
    this.currentSlideCount = 1;

    this.bindEvents();
    this.bindDrawerEvents();
    this.bindLightboxEvents();

    // Bidirectional sync with Cart Drawer
    const self = this;
    document.addEventListener('plantor:cart:sync', function (e) {
      if (e && e.detail && e.detail.cart) self.syncFromCartData(e.detail.cart);
    });
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && e.detail.cart) self.syncFromCartData(e.detail.cart);
    });

    this.syncFromExistingCart();
  }

  MixMatchBuilder.prototype.find = function (selector) {
    if (this.drawer) {
      const el = this.drawer.querySelector(selector);
      if (el) return el;
    }
    return this.container ? this.container.querySelector(selector) : null;
  };

  MixMatchBuilder.prototype.findAll = function (selector) {
    const list = Array.from(this.container.querySelectorAll(selector));
    if (this.drawer && this.drawer !== this.container && !this.container.contains(this.drawer)) {
      const drawerList = Array.from(this.drawer.querySelectorAll(selector));
      drawerList.forEach(el => {
        if (!list.includes(el)) list.push(el);
      });
    }
    return list;
  };

  MixMatchBuilder.prototype.openDrawer = function () {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.drawerOverlay) this.drawerOverlay.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.drawerOverlay) this.drawerOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('plantor-combo-drawer-open');
    document.body.style.overflow = 'hidden';
    this.syncFromExistingCart();
  };

  MixMatchBuilder.prototype.closeDrawer = function () {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.drawerOverlay) this.drawerOverlay.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    if (this.drawerOverlay) this.drawerOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('plantor-combo-drawer-open');
    document.body.style.overflow = '';
  };

  MixMatchBuilder.prototype.bindDrawerEvents = function () {
    const self = this;
    if (!this.isDrawerMode) return;

    // Open triggers: collection strip button & floating dock button
    document.querySelectorAll('[data-open-combo-drawer], #PlantorOpenComboDrawer').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.openDrawer();
      });
    });

    // Close trigger: close button inside drawer
    const closeBtn = document.getElementById('PlantorCloseComboDrawer');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.closeDrawer();
      });
    }

    // Close trigger: overlay click
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', function (e) {
        e.preventDefault();
        self.closeDrawer();
      });
    }

    // Close trigger: Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.drawer && self.drawer.classList.contains('is-open')) {
        self.closeDrawer();
      }
    });
  };

  MixMatchBuilder.prototype.bindEvents = function () {
    const self = this;

    // Top Tier Comparison Cards
    this.findAll('[data-mm-tier-card]').forEach(card => {
      card.addEventListener('click', function () {
        const grid = (self.drawer ? self.drawer.querySelector('.plantor-mm-grid') : null) || self.container.querySelector('.plantor-mm-grid');
        if (grid) {
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Card Add button
    this.findAll('[data-mm-add]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.disabled || this._isAdding) return;
        this._isAdding = true;
        setTimeout(() => { this._isAdding = false; }, 250);

        const card = this.closest('.plantor-mm-card');
        if (!card || card.classList.contains('is-sold-out')) return;
        self.addItem(card);
      });
    });

    // Card Plus button
    this.findAll('[data-mm-plus]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (this._isDebouncing) return;
        this._isDebouncing = true;
        setTimeout(() => { this._isDebouncing = false; }, 200);

        const card = this.closest('.plantor-mm-card');
        const variantId = card.getAttribute('data-variant-id');
        self.changeQty(variantId, 1);
      });
    });

    // Card Minus button
    this.findAll('[data-mm-minus]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (this._isDebouncing) return;
        this._isDebouncing = true;
        setTimeout(() => { this._isDebouncing = false; }, 200);

        const card = this.closest('.plantor-mm-card');
        const variantId = card.getAttribute('data-variant-id');
        self.changeQty(variantId, -1);
      });
    });

    // Quick View Trigger (button or clicking image/title)
    this.findAll('[data-mm-quickview]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const card = this.closest('.plantor-mm-card');
        self.openLightbox(card);
      });
    });

    this.findAll('[data-mm-open-lightbox]').forEach(el => {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const card = this.closest('.plantor-mm-card');
        self.openLightbox(card);
      });
    });

    // Tray Submit / Add to Cart
    if (this.traySubmitBtn) {
      this.traySubmitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.submitBundle();
      });
    }
  };

  MixMatchBuilder.prototype.bindLightboxEvents = function () {
    const self = this;
    if (!this.lightbox) return;

    // Close buttons and backdrop
    this.lightbox.querySelectorAll('[data-mm-lightbox-close]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.closeLightbox();
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.lightbox && self.lightbox.classList.contains('is-open')) {
        self.closeLightbox();
      }
    });

    // Gallery Arrows
    if (this.lightboxPrev) {
      this.lightboxPrev.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.goToSlide(self.currentSlideIndex - 1);
      });
    }

    if (this.lightboxNext) {
      this.lightboxNext.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.goToSlide(self.currentSlideIndex + 1);
      });
    }

    // Touch Swipe gestures for multi-image gallery on mobile
    if (this.lightboxGallery) {
      let touchStartX = 0;
      let touchStartY = 0;

      this.lightboxGallery.addEventListener('touchstart', function (e) {
        if (!e.touches || e.touches.length === 0) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      this.lightboxGallery.addEventListener('touchend', function (e) {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        const diffX = e.changedTouches[0].clientX - touchStartX;
        const diffY = e.changedTouches[0].clientY - touchStartY;

        // Verify horizontal swipe intent (>35px)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 35) {
          if (diffX < 0) {
            self.goToSlide(self.currentSlideIndex + 1);
          } else {
            self.goToSlide(self.currentSlideIndex - 1);
          }
        }
      }, { passive: true });
    }

    // Add to Combo Box inside Lightbox
    const btnBox = this.lightbox.querySelector('[data-mm-lightbox-add-box]');
    if (btnBox) {
      btnBox.addEventListener('click', function (e) {
        e.preventDefault();
        if (!self.currentLightboxCard) return;
        const variantId = self.currentLightboxCard.getAttribute('data-variant-id');
        if (self.items.has(variantId)) {
          self.changeQty(variantId, 1);
        } else {
          self.addItem(self.currentLightboxCard);
        }
        self.updateLightboxState();
      });
    }

    // Add Directly to Cart Drawer inside Lightbox
    const btnCart = this.lightbox.querySelector('[data-mm-lightbox-add-cart]');
    if (btnCart) {
      btnCart.addEventListener('click', function (e) {
        e.preventDefault();
        if (!self.currentLightboxCard) return;
        const variantId = self.currentLightboxCard.getAttribute('data-variant-id');
        const cartText = self.lightbox.querySelector('[data-mm-lightbox-cart-text]');
        if (cartText) cartText.textContent = 'Adding to Bag...';

        window._suppressCartDrawerOpen = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1
            }]
          })
        })
          .then(res => {
            if (!res.ok) throw new Error('Cart add failed');
            return res.json();
          })
          .then(() => {
            if (cartText) cartText.textContent = '✓ Added to Cart!';
            self.closeLightbox();
            if (window.PlantorCartDrawer && typeof window.PlantorCartDrawer.refresh === 'function') {
              window.PlantorCartDrawer.refresh(false);
            }
            const item = {
              title: self.currentLightboxCard.getAttribute('data-title') || '',
              image: self.currentLightboxCard.getAttribute('data-image') || '',
              price: parseInt(self.currentLightboxCard.getAttribute('data-price'), 10) || 0,
              card: self.currentLightboxCard
            };
            self.showItemNotification(item);
            setTimeout(() => {
              window._suppressCartDrawerOpen = false;
              if (cartText) cartText.textContent = 'Add to Cart Drawer';
            }, 2000);
          })
          .catch(err => {
            window._suppressCartDrawerOpen = false;
            console.error('Plantor Lightbox add to cart error:', err);
            if (cartText) cartText.textContent = 'Add to Cart Drawer';
            alert('Item could not be added to cart. Please check availability.');
          });
      });
    }
  };

  MixMatchBuilder.prototype.openLightbox = function (card) {
    if (!this.lightbox || !card) return;
    const self = this;
    this.currentLightboxCard = card;

    const variantId = card.getAttribute('data-variant-id');
    const price = parseInt(card.getAttribute('data-price'), 10) || 0;
    const comparePrice = parseInt(card.getAttribute('data-compare-price'), 10) || price;
    const title = card.getAttribute('data-title') || '';
    const description = card.getAttribute('data-description') || 'Hand-picked premium nursery specimen with healthy roots and vibrant foliage. Grown under natural sunlight and acclimatized for home living.';

    // Parse all product photos
    let images = [];
    try {
      const rawImages = card.getAttribute('data-images');
      if (rawImages) images = JSON.parse(rawImages);
    } catch (err) {
      images = [];
    }
    if (!images || images.length === 0) {
      const fallback = card.getAttribute('data-image-large') || card.getAttribute('data-image') || '';
      if (fallback) images = [fallback];
    }

    this.currentSlideIndex = 0;
    this.currentSlideCount = images.length;

    if (this.lightboxTrack) {
      this.lightboxTrack.innerHTML = images.map((src, i) => `
        <div class="plantor-mm-lightbox__slide${i === 0 ? ' is-active' : ''}">
          <img class="plantor-mm-lightbox__img" src="${src}" alt="${title} view ${i + 1}" loading="lazy" />
        </div>
      `).join('');
      this.lightboxTrack.style.transform = 'translateX(0%)';
    }

    if (this.lightboxDots) {
      this.lightboxDots.innerHTML = images.length > 1 ? images.map((_, i) => `
        <button type="button" class="plantor-mm-lightbox__dot${i === 0 ? ' is-active' : ''}" data-slide-index="${i}" aria-label="View photo ${i + 1}"></button>
      `).join('') : '';

      this.lightboxDots.querySelectorAll('.plantor-mm-lightbox__dot').forEach(dot => {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const idx = parseInt(this.getAttribute('data-slide-index'), 10);
          self.goToSlide(idx);
        });
      });
    }

    if (this.lightboxGallery) {
      if (images.length <= 1) {
        this.lightboxGallery.classList.add('is-single-image');
      } else {
        this.lightboxGallery.classList.remove('is-single-image');
      }
    }

    const titleEl = this.lightbox.querySelector('[data-mm-lightbox-title]');
    const priceEl = this.lightbox.querySelector('[data-mm-lightbox-price]');
    const compareEl = this.lightbox.querySelector('[data-mm-lightbox-compare]');
    const savingsEl = this.lightbox.querySelector('[data-mm-lightbox-savings]');
    const descEl = this.lightbox.querySelector('[data-mm-lightbox-desc]');

    if (titleEl) titleEl.textContent = title;
    if (priceEl) priceEl.textContent = formatMoney(price);

    if (compareEl) {
      if (comparePrice > price) {
        compareEl.style.display = 'inline';
        compareEl.textContent = formatMoney(comparePrice);
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (savingsEl) {
      // Do not display exact savings amount
      savingsEl.style.display = 'none';
    }

    if (descEl) descEl.textContent = description;

    this.updateLightboxState();

    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pmm-lightbox-open');
  };

  MixMatchBuilder.prototype.goToSlide = function (index) {
    if (this.currentSlideCount <= 1 || !this.lightboxTrack) return;
    if (index < 0) index = this.currentSlideCount - 1;
    if (index >= this.currentSlideCount) index = 0;
    this.currentSlideIndex = index;
    this.lightboxTrack.style.transform = `translateX(-${index * 100}%)`;

    if (this.lightboxDots) {
      const dots = this.lightboxDots.querySelectorAll('.plantor-mm-lightbox__dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }
  };

  MixMatchBuilder.prototype.updateLightboxState = function () {
    if (!this.lightbox || !this.currentLightboxCard) return;
    const variantId = this.currentLightboxCard.getAttribute('data-variant-id');
    const boxText = this.lightbox.querySelector('[data-mm-lightbox-box-text]');

    if (boxText) {
      if (this.items.has(variantId)) {
        const item = this.items.get(variantId);
        boxText.textContent = `✓ In Your Box (${item.quantity}) — Add Another`;
      } else {
        boxText.textContent = 'Add to Combo Box';
      }
    }
  };

  MixMatchBuilder.prototype.closeLightbox = function () {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pmm-lightbox-open');
    this.currentLightboxCard = null;
  };

  MixMatchBuilder.prototype.showItemNotification = function (item) {
    if (!item) return;
    const lineItem = {
      title: item.title,
      product_title: item.title,
      image: item.image,
      price: item.price,
      final_price: item.price,
      variant_title: (item.card && item.card.getAttribute('data-variant-title')) || ''
    };

    if (typeof window.plantorShowCartNotification === 'function') {
      window.plantorShowCartNotification(lineItem);
    } else if (window.ajaxCart && typeof window.ajaxCart.cartModalAdded === 'function') {
      window.ajaxCart.cartModalAdded(lineItem);
    }
  };

  MixMatchBuilder.prototype.addItem = function (card) {
    const variantId = card.getAttribute('data-variant-id');
    const price = parseInt(card.getAttribute('data-price'), 10) || 0;
    const compareAtPrice = parseInt(card.getAttribute('data-compare-price'), 10) || price;
    const title = card.getAttribute('data-title') || '';
    const image = card.getAttribute('data-image') || '';

    const currentQty = this.items.has(variantId) ? this.items.get(variantId).quantity : 0;
    const newQty = currentQty + 1;

    const itemData = {
      variantId: variantId,
      card: card,
      title: title,
      price: price,
      compareAtPrice: compareAtPrice,
      image: image,
      quantity: newQty,
      _optimisticTime: Date.now()
    };

    this.items.set(variantId, itemData);
    if (this._recentlyDeleted) {
      this._recentlyDeleted.delete(variantId);
    }

    // Direct synchronous DOM update for card to guarantee instant visual switch to 1
    card.classList.add('is-selected');
    const qtyVal = card.querySelector('[data-mm-qty-val]');
    if (qtyVal) qtyVal.textContent = newQty;
    const selectedBadge = card.querySelector('[data-mm-selected-badge]');
    if (selectedBadge) selectedBadge.style.display = 'inline-flex';
    const badgeQty = card.querySelector('[data-mm-card-qty-badge]');
    if (badgeQty) badgeQty.textContent = newQty;

    this.updateUI();
    this.syncItemCart(variantId, newQty);
    this.showItemNotification(itemData);
  };

  MixMatchBuilder.prototype.changeQty = function (variantId, delta) {
    if (!this.items.has(variantId)) return;
    const item = this.items.get(variantId);
    item.quantity += delta;
    item._optimisticTime = Date.now();

    let newQty = item.quantity;
    if (item.quantity <= 0) {
      this.items.delete(variantId);
      if (!this._recentlyDeleted) this._recentlyDeleted = new Map();
      this._recentlyDeleted.set(variantId, Date.now());
      newQty = 0;
      if (item.card) {
        item.card.classList.remove('is-selected');
        const qtyVal = item.card.querySelector('[data-mm-qty-val]');
        if (qtyVal) qtyVal.textContent = '1';
        const selectedBadge = item.card.querySelector('[data-mm-selected-badge]');
        if (selectedBadge) selectedBadge.style.display = 'none';
      }
    } else {
      if (item.card) {
        const qtyVal = item.card.querySelector('[data-mm-qty-val]');
        if (qtyVal) qtyVal.textContent = newQty;
        const badgeQty = item.card.querySelector('[data-mm-card-qty-badge]');
        if (badgeQty) badgeQty.textContent = newQty;
      }
    }
    this.updateUI();
    this.syncItemCart(variantId, newQty);
    if (delta > 0) {
      this.showItemNotification(item);
    }
  };

  MixMatchBuilder.prototype.getBundleProperties = function () {
    const campaignLower = ((this.campaignTitle || '') + ' ' + (this.festivalName || '')).toLowerCase();
    let boxType = 'plantor_combo';
    let tierCategory = 'combo';
    if (campaignLower.includes('hibi')) {
      boxType = 'hibiscus';
      tierCategory = 'hibiscus';
    } else if (campaignLower.includes('anth')) {
      boxType = 'anthurium';
      tierCategory = 'anthurium';
    } else if (campaignLower.includes('fruit')) {
      boxType = 'fruit';
      tierCategory = 'fruit';
    } else if (campaignLower.includes('rose') && !campaignLower.includes('desert rose') && !campaignLower.includes('adenium')) {
      boxType = 'rose';
      tierCategory = 'rose';
    }
    return {
      '_bundle_box': boxType,
      '_bundle_tier': tierCategory,
      '_bundle_name': this.campaignTitle
    };
  };

  MixMatchBuilder.prototype.syncItemCart = function (variantId, targetQty) {
    const self = this;
    const currentCart = (window.PlantorCartDrawer && window.PlantorCartDrawer.currentCart) || null;

    const performSync = function (cart) {
      const items = (cart && cart.items) ? cart.items : [];
      const existingItem = items.find(it => String(it.variant_id || it.id) === String(variantId));

      if (targetQty <= 0) {
        if (existingItem) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: 0 })
          })
            .then(r => r.json())
            .then(newCart => {
              if (window.PlantorCartDrawer && typeof window.PlantorCartDrawer.renderCart === 'function') {
                window.PlantorCartDrawer.renderCart(newCart);
                window.PlantorCartDrawer.triggerAppCartEvents(newCart);
              }
            });
        }
        return Promise.resolve();
      }

      if (existingItem) {
        if (existingItem.quantity === targetQty) {
          return Promise.resolve();
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity: targetQty })
        })
          .then(r => r.json())
          .then(newCart => {
            if (window.PlantorCartDrawer && typeof window.PlantorCartDrawer.renderCart === 'function') {
              window.PlantorCartDrawer.renderCart(newCart);
              window.PlantorCartDrawer.triggerAppCartEvents(newCart);
            }
          });
      } else {
        window._suppressCartDrawerOpen = true;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: targetQty,
              properties: self.getBundleProperties()
            }]
          })
        })
          .then(r => r.json())
          .then(() => {
            if (window.PlantorCartDrawer && typeof window.PlantorCartDrawer.refresh === 'function') {
              window.PlantorCartDrawer.refresh(false);
            }
            setTimeout(() => {
              window._suppressCartDrawerOpen = false;
            }, 600);
          })
          .catch(() => {
            window._suppressCartDrawerOpen = false;
          });
      }
    };

    if (currentCart) {
      performSync(currentCart).catch(err => console.warn('Fast cart sync notice:', err));
    } else {
      fetch('/cart.js', { headers: { 'Cache-Control': 'no-cache' } })
        .then(res => res.json())
        .then(cart => {
          if (window.PlantorCartDrawer) window.PlantorCartDrawer.currentCart = cart;
          return performSync(cart);
        })
        .catch(err => console.warn('Plantor real-time cart sync error:', err));
    }
  };

  MixMatchBuilder.prototype.syncCartAdd = function (variantId, qty) {
    this.syncItemCart(variantId, qty);
  };

  MixMatchBuilder.prototype.syncCartChange = function (variantId, newQty) {
    this.syncItemCart(variantId, newQty);
  };

  MixMatchBuilder.prototype.syncFromCartData = function (cart) {
    if (!cart) return;
    const self = this;
    const now = Date.now();

    // Preserve any optimistic items updated recently so lagging background /cart.js responses don't wipe them out
    const pendingOptimisticItems = new Map();
    this.items.forEach((item, vId) => {
      if (item._optimisticTime && (now - item._optimisticTime < 3500)) {
        pendingOptimisticItems.set(String(vId), item);
      }
    });

    const newItems = new Map();

    if (cart.items && cart.items.length) {
      cart.items.forEach(cartItem => {
        const vId = String(cartItem.variant_id || cartItem.id);

        // If user recently zeroed/deleted this item, do not resurrect it from a lagging cart snapshot
        if (self._recentlyDeleted && self._recentlyDeleted.has(vId)) {
          if (now - self._recentlyDeleted.get(vId) < 3500) {
            return;
          } else {
            self._recentlyDeleted.delete(vId);
          }
        }

        const card = self.find(`.plantor-mm-card[data-variant-id="${vId}"]`);
        if (card) {
          const price = parseInt(card.getAttribute('data-price'), 10) || cartItem.final_price;
          const compareAtPrice = parseInt(card.getAttribute('data-compare-price'), 10) || cartItem.original_price;
          const title = card.getAttribute('data-title') || cartItem.title;
          const image = card.getAttribute('data-image') || cartItem.image;

          let quantity = cartItem.quantity;
          let optimisticTime = 0;
          if (pendingOptimisticItems.has(vId)) {
            const pending = pendingOptimisticItems.get(vId);
            optimisticTime = pending._optimisticTime;
            // Retain the higher optimistic quantity if cart response is still lagging
            if (cartItem.quantity < pending.quantity) {
              quantity = pending.quantity;
            }
            pendingOptimisticItems.delete(vId);
          }

          newItems.set(vId, {
            variantId: vId,
            card: card,
            title: title,
            price: price,
            compareAtPrice: compareAtPrice,
            image: image,
            quantity: quantity,
            _optimisticTime: optimisticTime
          });
        }
      });
    }

    // Retain any pending optimistic items not yet present in cart.items
    pendingOptimisticItems.forEach((optItem, vId) => {
      if (optItem.quantity > 0) {
        newItems.set(String(vId), optItem);
      }
    });

    this.items = newItems;
    this.updateUI();
  };

  MixMatchBuilder.prototype.syncFromExistingCart = function () {
    const self = this;
    fetch('/cart.js', { headers: { 'Cache-Control': 'no-cache' } })
      .then(res => res.json())
      .then(cart => {
        if (window.PlantorCartDrawer) {
          window.PlantorCartDrawer.currentCart = cart;
        }
        self.syncFromCartData(cart);
      })
      .catch(err => console.warn('Could not read existing cart for combo:', err));
  };

  MixMatchBuilder.prototype.updateUI = function () {
    const self = this;
    let totalQty = 0;
    let originalSubtotal = 0;
    const unitList = [];

    // Sync card states & visual indicators
    this.findAll('.plantor-mm-card').forEach(card => {
      const vId = card.getAttribute('data-variant-id');
      const selectedBadge = card.querySelector('[data-mm-selected-badge]');
      const badgeQty = card.querySelector('[data-mm-card-qty-badge]');
      const qtyVal = card.querySelector('[data-mm-qty-val]');

      if (self.items.has(vId)) {
        const item = self.items.get(vId);
        card.classList.add('is-selected');
        if (selectedBadge) selectedBadge.style.display = 'inline-flex';
        if (badgeQty) badgeQty.textContent = item.quantity;
        if (qtyVal) qtyVal.textContent = item.quantity;
      } else {
        card.classList.remove('is-selected');
        if (selectedBadge) selectedBadge.style.display = 'none';
        if (qtyVal) qtyVal.textContent = '1';
      }
    });

    // Calculate totals & flatten units for visual slot display
    this.items.forEach(item => {
      totalQty += item.quantity;
      originalSubtotal += item.price * item.quantity;
      for (let i = 0; i < item.quantity; i++) {
        unitList.push(item);
      }
    });

    // Dynamic Tier & Price Calculations
    let dealPrice = originalSubtotal;
    let discountAmount = 0;
    let activeTier = null;
    let upsellNudgeHtml = '';
    const avgUnitPrice = totalQty > 0 ? originalSubtotal / totalQty : 54900;

    if (this.tier3Qty > 0 && totalQty >= this.tier3Qty) {
      // Tier 3 Active
      activeTier = { num: 3, label: `Tier 3 (${this.tier3Val}% OFF)` };
      if (this.tier3Type === 'fixed_price') {
        dealPrice = Math.round(this.tier3Val * 100);
      } else if (this.tier3Type === 'percentage') {
        discountAmount = Math.round(originalSubtotal * (this.tier3Val / 100));
        dealPrice = Math.max(0, originalSubtotal - discountAmount);
      } else {
        dealPrice = Math.max(0, originalSubtotal - Math.round(this.tier3Val * 100));
      }
      discountAmount = Math.max(0, originalSubtotal - dealPrice);
      upsellNudgeHtml = `<span class="nudge-highlight">🎉 Maximum Combo Savings Unlocked!</span>`;

    } else if (this.tier2Qty > 0 && totalQty >= this.tier2Qty) {
      // Tier 2 Active (e.g. 10 items)
      activeTier = { num: 2, label: `${this.tier2Qty} for ${formatMoney(this.tier2Val * 100)}` };
      if (this.tier2Type === 'fixed_price') {
        dealPrice = Math.round(this.tier2Val * 100);
      } else if (this.tier2Type === 'percentage') {
        discountAmount = Math.round(originalSubtotal * (this.tier2Val / 100));
        dealPrice = Math.max(0, originalSubtotal - discountAmount);
      } else {
        dealPrice = Math.max(0, originalSubtotal - Math.round(this.tier2Val * 100));
      }
      discountAmount = Math.max(0, originalSubtotal - dealPrice);

      if (this.tier3Qty > 0) {
        const moreNeeded = this.tier3Qty - totalQty;
        upsellNudgeHtml = `Add <span class="nudge-highlight">${moreNeeded} more</span> to unlock Tier 3 (${this.tier3Val}% OFF)!`;
      } else {
        upsellNudgeHtml = `<span class="nudge-highlight">🎉 Combo Complete! ${this.tier2Qty} for ${formatMoney(this.tier2Val * 100)}</span>`;
      }

    } else if (totalQty >= this.tier1Qty) {
      // Tier 1 Active (e.g. 5 items)
      activeTier = { num: 1, label: `${this.tier1Qty} for ${formatMoney(this.tier1Val * 100)}` };
      if (this.tier1Type === 'fixed_price') {
        dealPrice = Math.round(this.tier1Val * 100);
      } else if (this.tier1Type === 'percentage') {
        discountAmount = Math.round(originalSubtotal * (this.tier1Val / 100));
        dealPrice = Math.max(0, originalSubtotal - discountAmount);
      } else {
        dealPrice = Math.max(0, originalSubtotal - Math.round(this.tier1Val * 100));
      }
      discountAmount = Math.max(0, originalSubtotal - dealPrice);

      // Upsell Nudge to Tier 2 (e.g. 10 items)
      if (this.tier2Qty > 0) {
        const moreNeeded = this.tier2Qty - totalQty;
        upsellNudgeHtml = `Add <span class="nudge-highlight">${moreNeeded} more</span> to unlock <strong>${this.tier2Qty} for ${formatMoney(this.tier2Val * 100)}</strong>`;
      } else {
        upsellNudgeHtml = `<span class="nudge-highlight">🎉 ${this.tier1Qty} Plants Bundle Unlocked!</span>`;
      }

    } else {
      // Below Tier 1
      dealPrice = originalSubtotal;
      discountAmount = 0;
      const needed = this.tier1Qty - totalQty;
      const targetPromo = this.tier1Type === 'fixed_price' ? `${this.tier1Qty} for ${formatMoney(this.tier1Val * 100)}` : (this.tier1Type === 'percentage' ? `${this.tier1Val}% OFF` : `Flat ₹${this.tier1Val} OFF`);
      upsellNudgeHtml = `Add <span class="nudge-highlight">${needed} more plant${needed > 1 ? 's' : ''}</span> to unlock <strong>${targetPromo}</strong>`;
    }

    // Update Floating Dock Pill (if in drawer mode)
    if (this.isDrawerMode && this.floatingDock) {
      if (totalQty > 0) {
        if (this.floatingCount) {
          this.floatingCount.textContent = totalQty;
          this.floatingCount.style.display = 'inline-flex';
        }
        if (this.floatingSubtitle) {
          if (this.tier2Qty > 0 && totalQty >= this.tier2Qty) {
            this.floatingSubtitle.textContent = `🎉 ${this.tier2Qty} Plants Box Active (${formatMoney(dealPrice)})`;
          } else if (totalQty >= this.tier1Qty) {
            this.floatingSubtitle.textContent = `✓ ${this.tier1Qty} Plants Box Active (${formatMoney(dealPrice)})`;
          } else {
            this.floatingSubtitle.textContent = `${totalQty} of ${this.tier1Qty} plants selected`;
          }
        }
        if (this.floatingCtaText) {
          if (totalQty >= this.tier1Qty) {
            this.floatingCtaText.textContent = 'Review Box 🎁';
          } else {
            const left = this.tier1Qty - totalQty;
            this.floatingCtaText.textContent = `Add ${left} More ⚡`;
          }
        }
      } else {
        if (this.floatingCount) {
          this.floatingCount.style.display = 'none';
        }
        if (this.floatingSubtitle) {
          this.floatingSubtitle.textContent = this.badgeText;
        }
        if (this.floatingCtaText) {
          this.floatingCtaText.textContent = 'Build Box ⚡';
        }
      }
    }

    // Toggle Tray Visibility
    if (this.isDrawerMode) {
      if (this.tray) this.tray.classList.add('is-visible');
    } else {
      if (totalQty > 0) {
        if (this.tray) this.tray.classList.add('is-visible');
      } else {
        if (this.tray) this.tray.classList.remove('is-visible');
        return;
      }
    }

    // Render Visual Plant Slots
    if (this.traySlots) {
      this.traySlots.innerHTML = '';
      const targetSlots = (this.tier2Qty > 0 && totalQty >= this.tier1Qty) ? this.tier2Qty : this.tier1Qty;
      const displaySlots = Math.max(targetSlots, totalQty);

      for (let i = 0; i < displaySlots; i++) {
        const slotEl = document.createElement('div');
        if (i < unitList.length) {
          const unit = unitList[i];
          slotEl.className = 'plantor-mm-slot plantor-mm-slot--filled';
          slotEl.title = `${unit.title} - Click to remove`;
          slotEl.innerHTML = `
            <img class="plantor-mm-slot__img" src="${unit.image}" alt="${unit.title}" />
            <span class="plantor-mm-slot__remove">✕</span>
          `;
          slotEl.addEventListener('click', () => {
            self.changeQty(unit.variantId, -1);
          });
        } else {
          slotEl.className = 'plantor-mm-slot plantor-mm-slot--empty';
          slotEl.title = `Slot ${i + 1} (Empty)`;
        }
        this.traySlots.appendChild(slotEl);
      }
    }

    // Update Counts & Meta in Tray
    if (this.trayCountTitle) {
      this.trayCountTitle.textContent = `${totalQty} Plant${totalQty > 1 ? 's' : ''} in Box`;
    }
    if (this.trayCountSub) {
      this.trayCountSub.textContent = activeTier ? `✓ ${activeTier.label} Active` : `Pick at least ${this.tier1Qty} plants`;
    }

    // Update Nudge Text & Milestone Progress Fill
    if (this.trayNudgeText) {
      this.trayNudgeText.innerHTML = upsellNudgeHtml;
    }

    if (this.trayProgressFill) {
      const targetMax = (this.tier2Qty > 0 && totalQty >= this.tier1Qty) ? this.tier2Qty : this.tier1Qty;
      const pct = Math.min(100, Math.round((totalQty / targetMax) * 100));
      this.trayProgressFill.style.width = `${pct}%`;
    }

    // Update Pricing in Tray (Original Strikethrough Price if discounted)
    if (this.trayComparePrice) {
      if (discountAmount > 0) {
        this.trayComparePrice.style.display = 'inline';
        this.trayComparePrice.textContent = formatMoney(originalSubtotal);
      } else {
        this.trayComparePrice.style.display = 'none';
      }
    }

    if (this.trayDealPrice) {
      this.trayDealPrice.textContent = formatMoney(dealPrice);
    }

    if (this.traySavingsBadge) {
      this.traySavingsBadge.style.display = 'none';
    }

    // Enable / Disable Cart Submission Button
    if (this.traySubmitBtn) {
      const btnText = this.traySubmitBtn.querySelector('.pmm-btn-text');
      if (totalQty >= this.tier1Qty) {
        this.traySubmitBtn.disabled = false;
        if (btnText) {
          btnText.textContent = `${totalQty} Plants Box Active — View Cart`;
        }
      } else {
        this.traySubmitBtn.disabled = true;
        const missing = this.tier1Qty - totalQty;
        if (btnText) {
          btnText.textContent = `Pick ${missing} More Plant${missing > 1 ? 's' : ''}`;
        }
      }
    }

    // Highlight top tier comparison cards
    this.findAll('[data-mm-tier-card]').forEach(card => {
      const tierNum = parseInt(card.getAttribute('data-mm-tier-card'), 10);
      if (tierNum === 2) {
        card.classList.toggle('is-active', self.tier2Qty > 0 && totalQty >= self.tier2Qty);
      } else if (tierNum === 1) {
        card.classList.toggle('is-active', self.tier2Qty <= 0 || totalQty < self.tier2Qty);
      }
    });

    // Highlight top tier pills if present
    this.findAll('[data-mm-tier-pill]').forEach(pill => {
      const tierNum = parseInt(pill.getAttribute('data-mm-tier-pill'), 10);
      if (activeTier && activeTier.num >= tierNum) {
        pill.classList.add('is-active');
      } else {
        pill.classList.remove('is-active');
      }
    });

    this.activeTier = activeTier;
    this.dealPrice = dealPrice;
    this.savings = discountAmount;

    // Refresh Lightbox button state if open
    this.updateLightboxState();
  };

  MixMatchBuilder.prototype.ensureCartSync = function (callback) {
    const self = this;
    fetch('/cart.js', { headers: { 'Cache-Control': 'no-cache' } })
      .then(res => res.json())
      .then(cart => {
        const inCartCounts = {};
        if (cart.items && cart.items.length) {
          cart.items.forEach(it => {
            const vid = String(it.variant_id || it.id);
            inCartCounts[vid] = (inCartCounts[vid] || 0) + it.quantity;
          });
        }

        const missingToAdd = [];
        self.items.forEach((item, variantId) => {
          const vidStr = String(variantId);
          const currentInCart = inCartCounts[vidStr] || 0;
          if (currentInCart < item.quantity) {
            missingToAdd.push({
              id: parseInt(variantId, 10),
              quantity: item.quantity - currentInCart,
              properties: self.getBundleProperties()
            });
          }
        });

        if (missingToAdd.length > 0) {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: missingToAdd })
          })
            .then(res => res.json())
            .then(() => { if (callback) callback(); })
            .catch(() => { if (callback) callback(); });
        } else {
          if (callback) callback();
        }
      })
      .catch(() => { if (callback) callback(); });
  };

  MixMatchBuilder.prototype.submitBundle = function () {
    const self = this;
    const totalQty = Array.from(this.items.values()).reduce((sum, item) => sum + item.quantity, 0);
    if (totalQty < this.tier1Qty) return;

    if (this.traySubmitBtn) {
      const submitText = this.traySubmitBtn.querySelector('.pmm-btn-text');
      if (submitText) submitText.textContent = `Opening Cart...`;
    }

    if (this.isDrawerMode) {
      this.closeDrawer();
    }

    this.ensureCartSync(() => {
      self.openCartDrawer(totalQty);
    });
  };

  MixMatchBuilder.prototype.openCartDrawer = function (totalQty) {
    let opened = false;

    // Strategy 1: Primary PlantorCartDrawer
    if (window.PlantorCartDrawer) {
      if (typeof window.PlantorCartDrawer.open === 'function') {
        opened = window.PlantorCartDrawer.open() || false;
      }
      if (typeof window.PlantorCartDrawer.refresh === 'function') {
        window.PlantorCartDrawer.refresh(false);
      }
    }

    // Strategy 2: Direct DOM manipulation of Plantor drawer
    if (!opened) {
      const drawer = document.getElementById('PlantorCartDrawer');
      const overlay = document.getElementById('PlantorCartDrawerOverlay');
      if (drawer) {
        drawer.classList.add('is-open');
        if (overlay) overlay.classList.add('is-open');
        document.body.classList.add('plantor-drawer-open');
        document.body.style.overflow = 'hidden';
        opened = true;
      }
    }

    // Strategy 3: Header Cart click trigger (handles theme header cart bindings)
    if (!opened) {
      const headerCart = document.querySelector('.js-header-cart, .plantor-action-cart, #cart-icon-bubble');
      if (headerCart) {
        headerCart.click();
        opened = true;
      }
    }

    // Strategy 4: Native theme jQuery drawer event fallback
    if (!opened && window.jQuery && window.jQuery('.js-drawer').length) {
      window.jQuery('body').trigger('drawer.open');
      opened = true;
    }

    // Strategy 5: Fallback to cart page only if no drawer could be opened
    if (!opened) {
      window.location.href = '/cart';
    }

    setTimeout(() => {
      if (this.traySubmitBtn) {
        const submitText = this.traySubmitBtn.querySelector('.pmm-btn-text');
        if (submitText) {
          submitText.textContent = `${totalQty} Plants Box Active — View Cart`;
        }
      }
    }, 800);
  };

  // ---------------------------------------------------------------------------
  // 3. INITIALIZATION ON DOM READY
  // ---------------------------------------------------------------------------
  const activeBuilders = [];

  function init() {
    CountdownEngine.init();

    document.querySelectorAll('[data-plantor-mix-match-builder]').forEach(builderEl => {
      if (builderEl._mmInit) return;
      builderEl._mmInit = true;
      const b = new MixMatchBuilder(builderEl);
      activeBuilders.push(b);
    });
  }

  function syncAllFromCart(cart) {
    activeBuilders.forEach(b => {
      if (typeof b.syncFromCartData === 'function') {
        b.syncFromCartData(cart);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PlantorMixMatch = {
    init: init,
    syncAllFromCart: syncAllFromCart,
    CountdownEngine: CountdownEngine
  };
})();
